import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useCallback } from "react";
import { Form, Radio, Select, Spin, Checkbox, App } from "antd";
import { useFormSettings, useI18n, useUpsell } from "../../../contexts";
import { UpsellTag } from "../../upsell";
const FormSettingsCountryBypassTabContent = ()=>{
    const { message } = App.useApp();
    const { __, _x, _i } = useI18n();
    const { isPro, isLicensed } = useUpsell();
    const { isCountryBypass: isCountryBypassPersisted, countryBypassDbDownloadTime, onCountryBypassUpdateDatabase, iso3166OneAlpha2, predefinedCountryBypassLists } = useFormSettings();
    const [isUpdateDatabaseBusy, setUpdateDatabaseBusy] = useState(false);
    const handleUpdateDatabase = useCallback(async ()=>{
        setUpdateDatabaseBusy(true);
        try {
            await onCountryBypassUpdateDatabase();
            message.success(__("You successfully updated the IP-to-Country database."));
        } catch (e) {
            message.error(e.responseJSON.message);
            throw e;
        } finally{
            setUpdateDatabaseBusy(false);
        }
    }, [
        onCountryBypassUpdateDatabase
    ]);
    const getLabelForPredefinedCountryList = useCallback((code)=>{
        if (code === "GDPR") {
            return __("Countries where GDPR applies");
        }
        return __("Countries where %s applies", code);
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Geo-restriction"),
                style: {
                    marginBottom: 10
                },
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "isCountryBypass",
                        noStyle: true,
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    disabled: !isPro || !isLicensed,
                                    value: true,
                                    children: __("Enabled")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    disabled: !isPro || !isLicensed,
                                    value: false,
                                    children: __("Disabled")
                                })
                            ]
                        })
                    }),
                    isPro && !isLicensed && /*#__PURE__*/ _jsx("div", {
                        className: "notice notice-error inline below-h2 notice-alt",
                        style: {
                            margin: "10px 0"
                        },
                        children: /*#__PURE__*/ _jsxs("p", {
                            children: [
                                __("This functionality is only available with a valid license, as the IP-to-Country database must be downloaded regularly from our cloud service."),
                                " ",
                                "•",
                                " ",
                                /*#__PURE__*/ _jsx("a", {
                                    href: `#/licensing?navigateAfterActivation=${encodeURIComponent(window.location.href)}`,
                                    children: __("Activate license")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            margin: "5px 0px 0px"
                        },
                        children: /*#__PURE__*/ _jsx(UpsellTag, {
                            title: __("Enable geo-restriction?"),
                            testDrive: true,
                            feature: "country-bypass",
                            assetName: __("pro-modal/geo-restriction.png"),
                            description: __("Cookie banners must be displayed only to visitors from certain countries. For example, visitors from the EU. With geo-restriction you can specify exactly who sees a cookie banner and who doesn't, by country.")
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        children: __(`With the geo-restrictions you can specify that the cookie banner should be displayed only for users from certain countries. In many countries (outside the EU) it is not necessary to display a cookie banner. The location of the user is determined by their IP address.`)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, curValues)=>prevValues.isCountryBypass !== curValues.isCountryBypass,
                children: (param)=>{
                    let { getFieldValue } = param;
                    const isCountryBypass = getFieldValue("isCountryBypass");
                    return isPro && isCountryBypass && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                wrapperCol: {
                                    offset: 6
                                },
                                name: "countryBypassCheckboxTerms",
                                valuePropName: "checked",
                                style: {
                                    marginBottom: 15
                                },
                                rules: [
                                    {
                                        type: "boolean",
                                        required: true,
                                        transform: (value)=>value || undefined,
                                        message: __("Please confirm that you have read this!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    disabled: isCountryBypassPersisted,
                                    children: _i(_x("To determine the location of your website visitors, the MaxMind GeoLite2 database will be downloaded to your server and used from there. I have read and agree to the {{aMaxMindEndUserLicense}}MaxMind End User License Agreement{{/aMaxMindEndUserLicense}}. The download of the database is done via a devowl.io server and I agree to the {{aTerms}}terms and conditions{{/aTerms}} and {{aPrivacyPolicy}}privacy policy{{/aPrivacyPolicy}}.", "legal-text"), {
                                        aMaxMindEndUserLicense: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://www.maxmind.com/en/end-user-license-agreement"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        }),
                                        aTerms: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://devowl.io/terms/"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        }),
                                        aPrivacyPolicy: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://devowl.io/privacy-policy/"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        })
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                wrapperCol: {
                                    offset: 6
                                },
                                name: "countryBypassCheckboxAccuracy",
                                valuePropName: "checked",
                                style: {
                                    marginBottom: 15
                                },
                                rules: [
                                    {
                                        type: "boolean",
                                        required: true,
                                        transform: (value)=>value || undefined,
                                        message: __("Please confirm that you have read this!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    disabled: isCountryBypassPersisted,
                                    children: _i(_x("I understand that this type of location determination is not accurate with one hundred percent certainty. According to {{a}}MaxMind's data{{/a}}, 99.8%% accuracy is estimated without obligation.", "legal-text"), {
                                        a: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://support.maxmind.com/hc/en-us/articles/4407630607131-Geolocation-Accuracy"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        })
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Show banner only to users from these countries"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "countryBypassCountries",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsxs(Select, {
                                            mode: "multiple",
                                            showSearch: true,
                                            optionFilterProp: "children",
                                            children: [
                                                Object.keys(predefinedCountryBypassLists).map((code)=>{
                                                    const label = getLabelForPredefinedCountryList(code);
                                                    return label && /*#__PURE__*/ _jsx(Select.Option, {
                                                        value: code,
                                                        children: label
                                                    }, code);
                                                }),
                                                Object.keys(iso3166OneAlpha2).map((code)=>/*#__PURE__*/ _jsx(Select.Option, {
                                                        value: code,
                                                        children: iso3166OneAlpha2[code]
                                                    }, code))
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: __("Determines for users from which countries you want to display a cookie banner. It is recommended to display cookie banners at least in the countries where the GDPR and CCPA apply. The list of countries can be extended at any time by the accession of new countries (Last update: October 2023).")
                                    }),
                                    Object.keys(predefinedCountryBypassLists).map((code)=>{
                                        const label = getLabelForPredefinedCountryList(code);
                                        return label && /*#__PURE__*/ _jsxs("p", {
                                            className: "description",
                                            children: [
                                                /*#__PURE__*/ _jsxs("strong", {
                                                    children: [
                                                        label,
                                                        ": "
                                                    ]
                                                }),
                                                predefinedCountryBypassLists[code].map((cc)=>iso3166OneAlpha2[cc]).join(", ")
                                            ]
                                        }, code);
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Implicit consent for users from third countries"),
                                style: {
                                    marginBottom: 10
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "countryBypassType",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: "all",
                                                    children: __("Accept all")
                                                }),
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: "essentials",
                                                    children: __("Accept only essentials")
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: __(`Set what consent should be implied when a visitor is not shown a cookie banner.`)
                                    })
                                ]
                            }),
                            isCountryBypassPersisted && /*#__PURE__*/ _jsx(_Fragment, {
                                children: /*#__PURE__*/ _jsxs(Form.Item, {
                                    label: __("IP-to-Country database"),
                                    children: [
                                        /*#__PURE__*/ _jsx(Spin, {
                                            spinning: isUpdateDatabaseBusy,
                                            children: /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                className: "button",
                                                onClick: handleUpdateDatabase,
                                                children: __("Update now")
                                            })
                                        }),
                                        countryBypassDbDownloadTime ? /*#__PURE__*/ _jsx("p", {
                                            style: {
                                                margin: "10px 0 0 0"
                                            },
                                            children: /*#__PURE__*/ _jsx("strong", {
                                                children: __("You have downloaded the IP-to-Country database the last time on %s", new Date(countryBypassDbDownloadTime).toLocaleString(document.documentElement.lang))
                                            })
                                        }) : /*#__PURE__*/ _jsx("div", {
                                            className: "notice notice-error inline below-h2 notice-alt",
                                            style: {
                                                margin: "10px 0 0 0"
                                            },
                                            children: /*#__PURE__*/ _jsx("p", {
                                                children: __("Something seems to have gone wrong when downloading the IP-to-Country database. Please check if your server has a firewall configured and is blocking the download accordingly, or contact our support!")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "description",
                                            children: __("The IP-to-Country database contains a huge set of IP ranges where we can look up where the current user is coming from based on a specific IP address.")
                                        })
                                    ]
                                })
                            })
                        ]
                    });
                }
            })
        ]
    });
};
export { FormSettingsCountryBypassTabContent };
